import { styled } from '@mui/material';
import { Autocomplete, Box, Button, Form, Icon, Stack, Text, TextInput } from 'shell/components';
import { INSTITUTIONS, MAJORS, SEMESTER_OPTIONS } from 'utils/constants';

const LIST_OF_SCHOOLS = [...INSTITUTIONS, { name: 'Other' }].map(({ name }) => ({ key: name, value: name }));

const GeneralInformation = ({
    errors,
    form,
    isDeletable,
    handleChange,
    openDeleteDialog,
    handleSubmit,
    handleClose,
    isLti = false,
}) => {
    return (
        <GeneralInformationBox>
            <Stack spacing={4} sx={{ mt: 6 }}>
                <Stack direction={'row'} alignItems={'center'} justifyContent={'flex-start'}>
                    {!isLti && (
                        <Button startIcon={<Icon kind={'arrowLeft'} />} variant={'borderless'} onClick={handleClose}>
                            Back
                        </Button>
                    )}
                </Stack>
                <Stack spacing={1}>
                    <Text component={'h1'} variant={'h6'} weight={'medium'}>
                        Create a course
                    </Text>
                    <Text variant={'p1'} sx={{ textAlign: 'left' }}>
                        Please provide the course details below to create a course.
                    </Text>
                </Stack>
            </Stack>
            <Form onSubmit={() => handleSubmit()}>
                <InputStack spacing={6}>
                    <Autocomplete
                        data-cy={'institution-name-dropdown'}
                        disableClearable
                        value={form.institutionName}
                        label={'Institution Name'}
                        error={Boolean(errors.institutionName)}
                        helperText={errors.institutionName}
                        placeholder={'Select'}
                        required
                        options={LIST_OF_SCHOOLS}
                        virtualized
                        onChange={(_, selectedItem) => handleChange('institutionName', selectedItem)}
                    />
                    {form.institutionName?.value === 'Other' && (
                        <TextInput
                            error={Boolean(errors.otherInstitutionName)}
                            helperText={errors.otherInstitutionName}
                            label={'Please Specify:'}
                            onChange={(e) => handleChange('otherInstitutionName', e.target.value)}
                            placeholder={'Enter institution name'}
                            value={form.otherInstitutionName}
                            required
                        />
                    )}
                    <TextInput
                        error={Boolean(errors.faculty)}
                        helperText={errors.faculty}
                        label={'Department'}
                        onChange={(e) => handleChange('faculty', e.target.value)}
                        placeholder={'Enter a department'}
                        value={form.faculty}
                        required
                    />
                    <Autocomplete
                        disableClearable
                        error={Boolean(errors.fieldOfStudy)}
                        helperText={errors.fieldOfStudy}
                        placeholder={'Select'}
                        required={true}
                        value={form.fieldOfStudy}
                        label={'Field of Study'}
                        labelId={'field-of-study-select'}
                        options={MAJORS?.map((item) => {
                            return {
                                key: item.name,
                                value: item.name,
                            };
                        })}
                        onChange={(e, selectedItem) => handleChange('fieldOfStudy', selectedItem)}
                    />
                    <TextInput
                        error={Boolean(errors.courseName)}
                        helperText={errors.courseName}
                        label={'Course name'}
                        onChange={(e) => handleChange('courseName', e.target.value)}
                        placeholder={'Enter course name'}
                        value={form.courseName}
                        required
                    />
                    <Autocomplete
                        disableClearable
                        error={Boolean(errors.semester)}
                        helperText={errors.semester}
                        placeholder={'Select'}
                        required={true}
                        value={form.semester}
                        label={'Semester/Term'}
                        options={SEMESTER_OPTIONS?.map((item) => {
                            return {
                                key: item.key,
                                value: item.value,
                            };
                        })}
                        onChange={(_, selectedItem) => handleChange('semester', selectedItem)}
                    />
                    <TextInput
                        error={Boolean(errors.section)}
                        helperText={errors.section}
                        label={'Section (optional)'}
                        onChange={(e) => handleChange('section', e.target.value)}
                        placeholder={'Enter section'}
                        value={form.section}
                    />
                </InputStack>

                <Stack
                    sx={{ margin: (theme) => theme.spacing(6, 0, 2, 0) }}
                    alignItems={'center'}
                    direction={'row'}
                    justifyContent={'flex-end'}
                    spacing={4}
                >
                    <Button
                        color={'destructive'}
                        disabled={!isDeletable}
                        variant={'tertiary'}
                        onClick={openDeleteDialog}
                    >
                        Delete Course
                    </Button>
                    <Button data-cy={'general-information-next-button'} type={'submit'}>
                        Complete
                    </Button>
                </Stack>
            </Form>
        </GeneralInformationBox>
    );
};

const GeneralInformationBox = styled(Box)(({ theme }) => {
    return {
        width: '100%',
        height: '100%',
        background: theme.palette.ensightful.shades[0],
    };
});

const InputStack = styled(Stack)(({ theme }) => {
    return {
        margin: theme.spacing(6, 0, 2, 0),
    };
});

export default GeneralInformation;
